/**
 * Management-Block oben rechts (Produktentscheidung vom 27.09.2026).
 *
 * Bis v0.29 stand er als hohe Spalte am rechten Rand - direkt ueber der
 * Faehigkeitengruppe. Dadurch blieb der Kampfgruppe unten kein Platz mehr, sie
 * klebte in der Ecke, und das Ultimate lag am Handy unter dem Daumenballen.
 *
 * Jetzt ist er ein 2x2-Quadrat direkt unter dem gegnerischen Portalbalken:
 *
 *   ARMY        SHOP
 *   ENEMY LINE  SKILLS
 *
 * Links steht, was die LANE angeht, rechts, was den HELDEN angeht (Shop,
 * Skills). Darunter laeuft LAST WAVE ueber die ganze Breite und nennt, was
 * die letzte Welle GEKOSTET hat.
 *
 * Links unten stand bis zum 08.10.2026 DEFENSE. Mit dem Defense System ist
 * der Knopf weggefallen (GAME_DESIGN Abschnitt 10), und der Blick auf die
 * gegnerische Seite ist aus der unteren linken Ecke hierher gezogen - der
 * Platz war ohnehin fuer ihn vorgesehen (VISUAL_REFERENCE). Das Raster
 * bleibt damit bei vier Knoepfen; ein leerer Platz haette es schief
 * gemacht.
 *
 * Bis v0.51 stand darunter noch eine Symbolreihe, WAS geschickt wurde
 * (Produktentscheidung vom 29.09.2026, sie zu entfernen). Was drin war, sieht
 * man im Army-Fenster; in der Leiste zaehlt der Preis, weil man danach
 * entscheidet, ob man dieselbe Welle noch einmal schickt.
 *
 * Der Tausch ist bewusst: Die Kampfknoepfe unten werden deutlich besser
 * erreichbar, die Management-Knoepfe oben etwas schlechter. Army und Shop
 * drueckt man ZWISCHEN den Gefechten, die Faehigkeiten MITTENDRIN.
 *
 */

import { useEffect, useState } from 'react';
import { t } from '../i18n/index.js';
import { isFullscreen, toggleFullscreen, vollbildMoeglich } from './fullscreen.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';
import { formatGold, getWaveCooldown } from './economyView.js';
import { summarizeDraft } from './composerModel.js';
import { useHold, useLongPress, useTap } from './usePress.js';
import { SurrenderButton } from './SurrenderButton.js';
import { TonSymbol, VollbildSymbol } from './symbole.js';

/** Welcher Bereich gerade offen ist - fuer die Hervorhebung. */
export type ManagementSection = 'army' | 'shop' | 'talent' | null;

interface Props {
  snapshot: MatchSnapshot;
  active: ManagementSection;
  onOpenArmy: () => void;
  /**
   * Watch Frontline - seit 08.10.2026 im Raster, seit 09.10.2026 ein
   * Haltegriff: Er gilt nur, solange der Finger liegt.
   */
  enemyView: boolean;
  onWatchStart: () => void;
  onWatchEnd: () => void;
  onOpenShop: () => void;
  onOpenSkillTree: () => void;
  onResendLastWave: () => void;
  onEditLastWave: () => void;
}

function NavButton({
  label,
  hinweis,
  icon,
  active,
  alert,
  onClick,
}: {
  label: string;
  /** Laengerer Text fuer den Tooltip. Fehlt er, gilt das Label. */
  hinweis?: string;
  icon: string;
  active: boolean;
  alert?: boolean;
  onClick: () => void;
}) {
  // Nicht onClick: Wer mit dem linken Daumen laeuft, bekommt vom Browser
  // fuer den zweiten Finger gar kein click (aufgefallen am 27.09.2026).
  const press = useTap(onClick);

  return (
    <button
      type="button"
      title={hinweis ?? label}
      className={
        'dl-nav__button' +
        (active ? ' dl-nav__button--active' : '') +
        (alert ? ' dl-nav__button--alert' : '')
      }
      aria-pressed={active}
      {...press}
    >
      <span className="dl-nav__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="dl-nav__label">{label}</span>
    </button>
  );
}

/**
 * Dieselbe Kachel, aber zum Halten (Rueckmeldung vom 09.10.2026).
 *
 * Watch Frontline war bis dahin ein Umschalter: einmal tippen hin, einmal
 * tippen zurueck - und zusaetzlich kam die Kamera bei jeder Bewegung von
 * selbst zurueck. Das hat im Spiel gestoert, weil jeder Schritt den Blick
 * abgerissen hat. Jetzt gilt: Finger drauf = hinsehen, loslassen = zurueck.
 *
 * Die Beschriftung wechselt deshalb nicht mehr. Ein "BACK" waere gelogen:
 * Es gibt keine zweite Handlung, man laesst einfach los.
 */
function HoldNavButton({
  label,
  icon,
  active,
  onStart,
  onEnd,
}: {
  label: string;
  icon: string;
  active: boolean;
  onStart: () => void;
  onEnd: () => void;
}) {
  const halten = useHold(onStart, onEnd);

  return (
    <button
      type="button"
      title={label}
      className={'dl-nav__button' + (active ? ' dl-nav__button--active' : '')}
      aria-pressed={active}
      {...halten}
    >
      <span className="dl-nav__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="dl-nav__label">{label}</span>
    </button>
  );
}

export function ManagementNav({
  snapshot,
  active,
  onOpenArmy,
  enemyView,
  onWatchStart,
  onWatchEnd,
  onOpenShop,
  onOpenSkillTree,
  onResendLastWave,
  onEditLastWave,
}: Props) {
  const letzteWelle = snapshot.waveHistory[0] ?? null;
  const zusammenfassung = letzteWelle ? summarizeDraft(letzteWelle, snapshot) : null;

  // Kurz tippen schickt die letzte Welle erneut, lang druecken laedt sie in den
  // Composer (Masterprompt Abschnitt 74).
  const letzteWellePress = useLongPress(
    () => {
      if (letzteWelle) onResendLastWave();
    },
    () => {
      if (letzteWelle) onEditLastWave();
    },
  );

  const skilltreeOffen =
    snapshot.skillPoints > 0 || snapshot.talentPending || snapshot.capstonePending;

  const imSanctuary = snapshot.sanctuary === 'drinnen';

  /*
   * Der Army-Knopf leuchtet wie der Skills-Knopf, sobald wieder eine Armee
   * los darf (Rueckmeldung vom 11.10.2026). Dieselbe Regel wie das "GO" in
   * der Statusleiste - eine Wahrheit, zwei Stellen.
   */
  const armeeBereit = getWaveCooldown(snapshot.phase, snapshot.waveCooldown) === 0;

  return (
    <nav className="dl-nav" aria-label={t.army.open}>
      {/* Letzte Welle: eine Management-Aktion, aber keine eigene Ansicht.
          Sie steht ueber dem Quadrat und laeuft ueber beide Spalten
          (Rueckmeldung vom 27.09.2026). */}
      <button
        type="button"
        className={'dl-nav__last' + (letzteWelle ? '' : ' dl-nav__last--dim')}
        aria-disabled={!letzteWelle}
        title={t.army.lastWaveHint}
        {...letzteWellePress}
      >
        <span className="dl-nav__last-head">
          <span className="dl-nav__last-title">{t.army.lastWave}</span>
          <span className="dl-nav__last-detail">
            {zusammenfassung
              ? `${formatGold(zusammenfassung.cost)} ${t.economy.gold}`
              : t.army.lastWaveNone}
          </span>
        </span>
      </button>

      {/* Reihenfolge im Markup = spaltenweise, weil das Raster spaltenweise
          fuellt: links die Lane, rechts der Held. */}
      <NavButton
        label={t.army.open}
        icon="⚔"
        active={active === 'army'}
        alert={armeeBereit}
        onClick={onOpenArmy}
      />
      {/*
        Watch Frontline (Abschnitt 13). Hier stand bis zum 09.10.2026 die
        Enemy View - eine Ansicht der gegnerischen Lane, die es auf der
        gemeinsamen Lane nicht mehr gibt.
      */}
      <HoldNavButton
        label={t.frontline.watchKurz}
        icon="👁"
        active={enemyView}
        onStart={onWatchStart}
        onEnd={onWatchEnd}
      />
      {/*
        Der Shop liegt im Sanctuary (GAME_DESIGN Abschnitt 5f). Der Knopf
        bleibt bedienbar - man soll hineinsehen und planen koennen -, sagt
        aber, warum gerade nichts geht.
      */}
      <NavButton
        label={t.shop.open}
        hinweis={imSanctuary ? t.shop.open : t.sanctuary.shopLocked}
        icon={imSanctuary ? '⌂' : '🔒'}
        active={active === 'shop'}
        onClick={onOpenShop}
      />
      <NavButton
        label={t.shop.openSkills}
        icon="✦"
        active={active === 'talent'}
        alert={skilltreeOffen}
        onClick={onOpenSkillTree}
      />
    </nav>
  );
}

/**
 * Die seltenen Schalter: Aufgeben, Vollbild und Ton.
 *
 * Sie sitzen oben links, weit weg von Joystick und Faehigkeiten - ein
 * versehentliches Aufgeben mitten im Gefecht waere sonst zu leicht passiert
 * (Rueckmeldung vom 26.09.2026).
 *
 * Seit dem 13.10.2026 als Symbole in einer Reihe, von links: Aufgeben,
 * Vollbild, Ton. Die Woerter stehen weiter als Tooltip und fuer Screenreader
 * daran.
 */
export function MinorControls({
  stumm,
  onToggleSound,
  onSurrender,
  canSurrender,
}: {
  stumm: boolean;
  onToggleSound: () => void;
  onSurrender: () => void;
  canSurrender: boolean;
}) {
  const [vollbild, setVollbild] = useState(isFullscreen);

  useEffect(() => {
    const merken = () => setVollbild(isFullscreen());
    document.addEventListener('fullscreenchange', merken);
    return () => document.removeEventListener('fullscreenchange', merken);
  }, []);

  const vollbildText = vollbild ? t.fullscreen.exit : t.fullscreen.enter;
  const tonText = stumm ? t.sound.off : t.sound.on;

  return (
    <div className="dl-hud__minor">
      {/*
        Ohne Match gibt es nichts aufzugeben. Dann haelt ein leerer Platz die
        Stelle frei - sonst rutschten Vollbild und Ton nach links, sobald das
        Match beginnt.
      */}
      {canSurrender ? (
        <SurrenderButton onSurrender={onSurrender} />
      ) : (
        <span className="dl-hud__minor-platz" aria-hidden="true" />
      )}
      {/*
        Vollbild: Am Handy frisst die Adressleiste oben ein Viertel der Hoehe,
        und die Gestenleiste liegt im Querformat ueber dem rechten Rand
        (Rueckmeldung vom 26.09.2026). Beim Matchstart wird es von selbst
        angefragt - hier laesst es sich jederzeit umschalten.

        NUR WO ES GEHT (15.10.2026): Auf dem iPhone erlaubt der Browser kein
        Vollbild. Dort stand das Symbol und tat nichts - und wer es drueckt
        und nichts geschieht, haelt das Spiel fuer kaputt. Den Weg ueber den
        Home-Bildschirm erklaeren das Menue und der Hinweis beim ersten
        Start.
      */}
      {vollbildMoeglich() && (
        <button
          type="button"
          className="dl-nav__small"
          title={vollbildText}
          aria-label={vollbildText}
          onClick={() => void toggleFullscreen()}
        >
          <VollbildSymbol aktiv={vollbild} />
        </button>
      )}
      <button
        type="button"
        className="dl-nav__small"
        title={tonText}
        aria-label={tonText}
        aria-pressed={!stumm}
        onClick={onToggleSound}
      >
        <TonSymbol an={!stumm} />
      </button>
    </div>
  );
}
